import React, { useState } from 'react';
import { FaBell, FaEnvelope, FaSms, FaSlack, FaCog, FaTrash, FaPlus } from 'react-icons/fa';
import { useMyContext } from '../MyContext';

const dispositions = [
  "Solicit Gifts","Bankruptcy", "Attorney", "Dispute", "Fraud", "Threat", "Incarcerated", "Refuse to Pay", "Third Party", 
  "Do Not Call", "Settlement Offer", "Payment Promise", "Medical Hardship", "Deceased", "Wrong Number", 
  "Callback Requested", "Language Barrier", "Escalation Requested", "Harassment Complaint", 
  "Cease & Desist", "Unemployment"
];

const notificationOptions = [
  { label: 'Email', icon: <FaEnvelope /> },
  { label: 'SMS', icon: <FaSms /> },
  { label: 'Slack', icon: <FaSlack /> }
];

const ConfigurationTab = () => {
  const { clientCodes } = useMyContext();
  const [selectedClient, setSelectedClient] = useState('ALL');
  const [selectedDispositions, setSelectedDispositions] = useState([]);
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [newSubscriber, setNewSubscriber] = useState('');

  const toggleDisposition = (disposition) => {
    setSelectedDispositions(prev =>
      prev.includes(disposition)
        ? prev.filter(d => d !== disposition)
        : [...prev, disposition]
    );
  };

  const toggleNotification = (type) => {
    setNotificationTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleAddSubscriber = () => {
    if (newSubscriber && !subscribers.find(sub => sub.email === newSubscriber)) {
      setSubscribers(prev => [...prev, { email: newSubscriber, active: true }]);
      setNewSubscriber('');
    }
  };

  const handleToggleSubscriber = (email) => {
    setSubscribers(prev =>
      prev.map(sub => sub.email === email ? { ...sub, active: !sub.active } : sub)
    );
  };

  const handleRemoveSubscriber = (email) => {
    setSubscribers(prev => prev.filter(sub => sub.email !== email));
  };

  return (
    <div className="p-6">
      <h3 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
        <FaCog className="mr-2 text-blue-600" /> Notification Configuration
      </h3>

      {/* Client Selector */}
      <div className="mb-4">
        <label className="block font-medium mb-1">Select Client</label>
        <select
          value={selectedClient}
          onChange={(e) => setSelectedClient(e.target.value)}
          className="p-2 border rounded w-full"
        >
          <option value="ALL">All Clients</option>
          {clientCodes.map((client) => (
            <option key={client.clientcode} value={client.clientcode}>
              {client.name}
            </option>
          ))}
        </select>
      </div>

      {/* Call Dispositions */}
      <div className="mb-4">
        <label className="block font-medium mb-2">Select Call Dispositions</label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {dispositions.map((d, idx) => (
            <label key={idx} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={selectedDispositions.includes(d)}
                onChange={() => toggleDisposition(d)}
              />
              <span>{d}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Notification Types */}
      <div className="mb-6">
        <label className="block font-medium mb-2">Notification Channels</label>
        <div className="flex gap-4 flex-wrap">
          {notificationOptions.map(({ label, icon }) => (
            <button
              key={label}
              onClick={() => toggleNotification(label)}
              className={`flex items-center space-x-2 px-4 py-2 border rounded ${
                notificationTypes.includes(label)
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700'
              }`}
            >
              {icon}
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Email Subscribers */}
      <div className="mb-6">
        <label className="block font-medium mb-2">Email Subscribers</label>
        <div className="flex mb-3">
          <input
            type="email"
            value={newSubscriber}
            onChange={(e) => setNewSubscriber(e.target.value)}
            placeholder="Enter email"
            className="p-2 border rounded w-full mr-2"
          />
          <button
            onClick={handleAddSubscriber}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center"
          >
            <FaPlus className="mr-1" /> Add
          </button>
        </div>
        {subscribers.length > 0 && (
          <ul className="space-y-2">
            {subscribers.map((sub, idx) => (
              <li key={idx} className="flex items-center justify-between bg-white border p-2 rounded shadow-sm">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={sub.active}
                    onChange={() => handleToggleSubscriber(sub.email)}
                  />
                  <span className={sub.active ? 'text-gray-800' : 'text-gray-400 line-through'}>
                    {sub.email}
                  </span>
                </label>
                <button
                  onClick={() => handleRemoveSubscriber(sub.email)}
                  className="text-red-600 hover:text-red-800"
                  title="Remove"
                >
                  <FaTrash />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Summary */}
      <div className="bg-gray-100 p-4 rounded shadow-sm">
        <h4 className="font-semibold mb-2">Current Settings:</h4>
        <p>
          <strong>Client:</strong> {selectedClient === 'ALL' ? 'All Clients' : selectedClient}
        </p>
        <p>
          <strong>Dispositions:</strong>{' '}
          {selectedDispositions.length > 0 ? selectedDispositions.join(', ') : 'None selected'}
        </p>
        <p>
          <strong>Notifications:</strong>{' '}
          {notificationTypes.length > 0 ? notificationTypes.join(', ') : 'None selected'}
        </p>
        <p>
          <strong>Subscribers:</strong>{' '}
          {subscribers.filter(s => s.active).map(s => s.email).join(', ') || 'None selected'}
        </p>
      </div>
    </div>
  );
};

export default ConfigurationTab;

